'use client';

import React from 'react';
import { styled } from '@linaria/react';
import { ExternalLink } from 'lucide-react';

import type { ContentData } from '@/types/content.types';

import Link from '@/components/Link';
import VisuallyHidden from '@/components/VisuallyHidden';
import PoppingArrows from './PoppingArrows';

interface Props extends React.HTMLAttributes<HTMLAnchorElement> {
  contentData: ContentData;
  includeEnterAnimation?: boolean;
}

function ContentPreview({
  contentData,
  includeEnterAnimation,
  ...delegated
}: Props) {
  const {
    title,
    subtitle,
    abstract,
    pathname,
    externalUrl,
    externalLabel,
  } = contentData;

  const [isHoveringOverCard, setIsHoveringOverCard] =
    React.useState(false);

  const isExternal = !!externalLabel;

  const href = isExternal ? externalUrl : pathname;

  return (
    <Wrapper
      {...delegated}
      data-include-enter-animation={includeEnterAnimation}
      onMouseEnter={() => setIsHoveringOverCard(true)}
      onMouseLeave={() => setIsHoveringOverCard(false)}
    >
      <Title href={href}>
        <span dangerouslySetInnerHTML={{ __html: title }} />
        {isExternal && <StyledExternalLink size={18} />}
      </Title>
      {subtitle && (
        <Subheading dangerouslySetInnerHTML={{ __html: subtitle }} />
      )}

      <Excerpt dangerouslySetInnerHTML={{ __html: abstract }} />

      <PoppingArrows
        href={href}
        shouldShowFirstArrow={isHoveringOverCard}
      >
        {isExternal ? (
          `Read on ${externalLabel}`
        ) : (
          <>
            Read more
            <VisuallyHidden>: {title}</VisuallyHidden>
          </>
        )}
      </PoppingArrows>
    </Wrapper>
  );
}

const Wrapper = styled.article`
  position: relative;
  display: block;
  text-decoration: none;
  color: var(--color-text);

  &[data-include-enter-animation='true'] {
    animation:
      entrez 900ms cubic-bezier(0.17, 0.67, 0.34, 1),
      fadeIn 500ms;

    @media (prefers-reduced-motion: reduce) {
      animation: fadeIn 500ms;
    }
  }

  @keyframes entrez {
    from {
      transform: translateY(-32px);
    }
  }
`;

const Title = styled(Link)`
  display: flex;
  align-items: baseline;
  gap: 12px;
  width: fit-content;
  padding-inline: 4px;
  margin-inline: -4px;
  border-radius: 2px;
  color: inherit;
  text-decoration: none;
  font-size: 1.375rem;
  font-weight: var(--font-weight-bold);
  outline-offset: 4px;
  outline-color: var(--color-primary);

  &:hover {
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-decoration-color: var(--color-primary);
    text-underline-offset: 2px;
  }

  /*
    I can add certain HTML elements to the title, in the markdown.
    NOTE: This code is semi-duplicated in /components/BlogPostTitle
  */
  code {
    position: relative;
    font-family: var(--font-family-mono);
    font-size: 0.9375em;
    display: inline-block;
    background: var(--color-cloud-300);
    padding-inline: 0.25em;
    border-radius: 4px;
  }
  &:hover code {
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-decoration-color: var(--color-primary);
    text-underline-offset: 2px;
  }

  html[data-color-mode='dark'] & code {
    background: var(--color-gray-200);
  }
`;

const Subheading = styled.p`
  color: var(--color-gray-700);
  font-weight: var(--font-weight-medium);
  font-size: calc(17 / 16 * 1rem);

  code {
    font-family: var(--font-family-mono);
    font-size: 0.9375em;
    font-weight: var(--font-weight-bold);
  }
`;

const Excerpt = styled.p`
  font-size: 1rem;
  margin-block: 16px;
  white-space: pre-wrap;

  code {
    /* Copied from InlineCode.tsx */
    display: inline;
    font-family: var(--font-family-mono);
    font-size: 0.9375em;
    font-style: normal;
    letter-spacing: -0.5px;
    padding: 2px 6px;
    margin: 1px -1px;
    background: var(--color-code-bg);
    border-radius: 3px;
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
  }
`;

const StyledExternalLink = styled(ExternalLink)`
  color: var(--color-gray-500);
  transform: translateY(1.5px);
`;

export default ContentPreview;
